import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import * as global from '../data/global';
import { MuiMaterials } from '../@index.js';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

const CablePicker = (props) => {
    const cables = global.gw.selectedSite.cables || [];
    const bins = global.gw.selectedSite.data?.bins || [];
    
    const [isArrayChanged, setIsArrayChanged] = useState(false);
    const [selectedNames, setSelectedNames] = useState(cables.map(b => { return b.name; }));
    const [allChecked, setAllChecked] = useState(cables.slice().fill(true));
    const [allCheckboxChecked, setAllCheckboxChecked] = useState(true);
    const [forceUpdate, setForceUpdate] = useState(0);

    // Helper function to get theme-aware colors (reads directly from global.gw.theme)
    const getThemeColor = useCallback((lightColor, darkColor) => {
        if (global.gw.theme === 'light') {
            return lightColor;
        }
        return darkColor;
    }, []);

    // Listen for theme changes - only trigger re-render when theme actually changes
    useEffect(() => {
        const handleThemeChange = () => {
            setForceUpdate(prev => prev + 1);
        };
        window.addEventListener('themeChanged', handleThemeChange);
        return () => window.removeEventListener('themeChanged', handleThemeChange);
    }, []);
    
    const cableTriggerRef = useRef(null);
    const cableLabelRef = useRef(null);
    const cableListRef = useRef(null);
    const cablePanelRef = useRef(null);
    const [menuOpen, setMenuOpen] = useState(false);

    const positionCableListPanel = useCallback(() => {
        const trigger = cableTriggerRef.current;
        const label = cableLabelRef.current;
        const panel = cablePanelRef.current;
        if (!trigger || !panel) return;
        const anchor = label || trigger;
        const rect = anchor.getBoundingClientRect();
        const maxHeight = Math.max(120, window.innerHeight - rect.bottom - 12);
        panel.style.top = `${rect.bottom + 4}px`;
        panel.style.left = `${rect.left}px`;
        panel.style.minWidth = `${Math.max(rect.width, 160)}px`;
        panel.style.width = "max-content";
        panel.style.maxWidth = `${Math.max(160, window.innerWidth - rect.left - 8)}px`;
        panel.style.maxHeight = `${maxHeight}px`;
    }, []);

    const closeCableList = useCallback(() => {
        const cableList = cableListRef.current;
        if (!cableList || cableList.style.display === "none") return;
        cableList.style.display = "none";
        setMenuOpen(false);
    }, []);

    const toggleCableList = useCallback(() => {
        const cableList = cableListRef.current;
        if (!cableList) return;
        if (cableList.style.display === "none" || cableList.style.display === "") {
            cableList.style.display = "unset";
            setMenuOpen(true);
            positionCableListPanel();
        } else {
            cableList.style.display = "none";
            setMenuOpen(false);
        }
    }, [positionCableListPanel]);

    useEffect(() => {
        const handlePointerDown = (event) => {
            const cableList = cableListRef.current;
            if (!cableList || cableList.style.display === "none") return;
            const target = event.target;
            if (cableTriggerRef.current?.contains(target)) return;
            if (target.closest?.(".cableListPanel")) return;
            closeCableList();
        };
        document.addEventListener("mousedown", handlePointerDown);
        return () => document.removeEventListener("mousedown", handlePointerDown);
    }, [closeCableList]);

    const selectionSummary = useMemo(() => {
        if (!cables.length) return "None";
        const checkedCount = allChecked.filter(Boolean).length;
        if (checkedCount === 0) return "None";
        if (checkedCount === cables.length) return "All";
        return `${checkedCount} selected`;
    }, [cables.length, allChecked]);

    const fieldSx = useMemo(() => ({
        width: "auto",
        minWidth: 140,
        maxWidth: 200,
        flex: "0 0 auto",
        '& .MuiOutlinedInput-root': {
            color: getThemeColor('#333333', '#f4cc8e'),
            backgroundColor: getThemeColor('#ffffff', '#2d2d2d'),
            fontSize: '14px',
            cursor: 'pointer',
            '& fieldset': { borderColor: getThemeColor('#cccccc', 'rgba(244,204,142,0.5)') },
            '&:hover fieldset': { borderColor: getThemeColor('#666666', '#f4cc8e') },
            '&.Mui-focused fieldset': { borderColor: getThemeColor('#666666', '#f4cc8e') },
        },
        '& .MuiInputLabel-root': { color: getThemeColor('#666666', '#a8abb1'), fontSize: '14px' },
        '& .MuiInputLabel-root.Mui-focused': { color: getThemeColor('#333333', '#f4cc8e') },
        '& .MuiInputBase-input': {
            color: getThemeColor('#333333', '#f4cc8e'),
            fontSize: '14px',
            cursor: 'pointer',
            paddingTop: '8.5px',
            paddingBottom: '8.5px',
        },
        '& .MuiSvgIcon-root': { color: getThemeColor('#666666', '#a8abb1') },
    }), [getThemeColor, forceUpdate]);
    
    // Get cable IDs in selected bins (for "All" checkbox calculation)
    const getCableIdsInSelectedBins = useMemo(() => {
        const cableIdsInSelectedBins = new Set();
        if (props.selectedBins && props.selectedBins.length > 0 && bins && bins.length > 0) {
            bins.forEach(bin => {
                if (props.selectedBins.includes(bin.binId) && bin.cableList) {
                    bin.cableList.forEach(cable => {
                        cableIdsInSelectedBins.add(cable.id);
                    });
                }
            });
        }
        return cableIdsInSelectedBins;
    }, [props.selectedBins, bins]);
    
    // Always show all cables (don't filter them out)
    const filteredCables = cables;
    
    // Track if this is the initial mount
    const isInitialMount = useRef(true);
    
    // Auto-select/deselect cables when bins are selected/deselected
    useEffect(() => {
        let tempChecked = [...allChecked];
        let changed = false;
        const isInitial = isInitialMount.current;
        
        if (isInitial) {
            isInitialMount.current = false;
        }
        
        if (props.selectedBins && props.selectedBins.length > 0 && getCableIdsInSelectedBins.size > 0) {
            // Bins are selected - ensure all cables in selected bins are checked
            getCableIdsInSelectedBins.forEach(cableId => {
                const idx = cables.findIndex(c => c.id === cableId);
                if (idx >= 0 && !tempChecked[idx]) {
                    tempChecked[idx] = true;
                    changed = true;
                }
            });
            
            // After initial mount, uncheck cables NOT in selected bins
            // On initial mount, if all bins are selected, ensure ALL cables are checked
            if (isInitial) {
                // Check if all bins are selected by comparing with total number of bins
                const allBinsSelected = bins.length > 0 && props.selectedBins.length === bins.length;
                if (allBinsSelected) {
                    // All bins selected - ensure ALL cables are checked
                    cables.forEach((cable, idx) => {
                        if (!tempChecked[idx]) {
                            tempChecked[idx] = true;
                            changed = true;
                        }
                    });
                }
            } else {
                // After initial mount, uncheck cables NOT in selected bins
                cables.forEach((cable, idx) => {
                    if (!getCableIdsInSelectedBins.has(cable.id) && tempChecked[idx]) {
                        tempChecked[idx] = false;
                        changed = true;
                    }
                });
            }
        } else {
            // No bins selected - uncheck all cables (only if not initial mount with bins selected)
            if (!isInitial) {
                cables.forEach((cable, idx) => {
                    if (tempChecked[idx]) {
                        tempChecked[idx] = false;
                        changed = true;
                    }
                });
            }
        }
        
        if (changed) {
            setAllChecked(tempChecked);
            
            // Update selectedNames and trigger cable selection callbacks
            const selectedCableNames = cables.filter((c, idx) => tempChecked[idx]).map(c => c.name);
            setSelectedNames(selectedCableNames);
            
            // Trigger selection callbacks for cables that changed (skip on initial mount to avoid double calls)
            if (!isInitial) {
                cables.forEach((cable, idx) => {
                    const wasChecked = allChecked[idx];
                    const isChecked = tempChecked[idx];
                    if (wasChecked !== isChecked && props && props.selected) {
                        props.selected(cable.id, isChecked);
                    }
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedBins, getCableIdsInSelectedBins]);
    
    // Update allCheckboxChecked - always check if ALL visible cables are checked
    // Since all cables are visible (not filtered), check if ALL cables are checked
    useEffect(() => {
        // Always check if ALL cables are checked (since all cables are visible)
        if (cables.length === 0) {
            setAllCheckboxChecked(false);
            if (props.onAllCheckboxChange) {
                props.onAllCheckboxChange(false);
            }
        } else {
            // Check if ALL cables are actually checked
            const allCablesChecked = cables.length > 0 && allChecked.length === cables.length && allChecked.every(checked => checked === true);
            setAllCheckboxChecked(allCablesChecked);
            if (props.onAllCheckboxChange) {
                props.onAllCheckboxChange(allCablesChecked);
            }
        }
    }, [allChecked, cables]);
    
    // Sync with bin picker's "All" checkbox
    useEffect(() => {
        if (props.syncAllCheckbox !== undefined && props.syncAllCheckbox !== allCheckboxChecked) {
            setAllCheckboxChecked(props.syncAllCheckbox);
        }
    }, [props.syncAllCheckbox]);

    const theme = useMemo(() => {
        const isLight = global.gw.theme === 'light';
        return MuiMaterials.createTheme({
            components: {
                MuiInputLabel: {
                    styleOverrides: {
                        root: {
                            color: isLight ? '#000000' : '#f4cc8e',
                            fontSize: "12px",
                            ...(isLight && {
                                '&:not(.MuiInputLabel-shrink)': {
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    left: '14px',
                                },
                            }),
                        },
                        shrink: {
                            top: isLight ? '8px' : undefined,
                        },
                    }
                },
                MuiButton: {
                    styleOverrides: {
                        root: {
                            fontSize: "12px",
                            background: isLight ? '#e0e0e0' : '#2d2d2d',
                            color: isLight ? '#333333' : 'rgb(244, 204, 142)',
                        }
                    }
                },
                MuiIconButton: {
                    styleOverrides: {
                        sizeMedium: {
                            color: isLight ? '#333333' : 'rgb(244, 204, 142)'
                        }
                    }
                },
            },
        });
    }, [forceUpdate]);

    return (
        <MuiMaterials.Box
            style={props.windowStyle}
            sx={{ display: "flex", alignItems: "center", width: "auto", flex: "0 0 auto", minWidth: 0 }}
        >
            <MuiMaterials.FormControl
                ref={cableTriggerRef}
                size="small"
                variant="outlined"
                sx={fieldSx}
            >
                <MuiMaterials.InputLabel id="scan-cables-label" shrink>
                    Cables
                </MuiMaterials.InputLabel>
                <MuiMaterials.OutlinedInput
                    ref={cableLabelRef}
                    label="Cables"
                    notched
                    readOnly
                    value={selectionSummary}
                    onClick={toggleCableList}
                    endAdornment={(
                        <MuiMaterials.Box
                            component="span"
                            sx={{ display: "flex", alignItems: "center", pointerEvents: "none", mr: 0.25 }}
                        >
                            {menuOpen ? <ArrowDropUpIcon fontSize="small" /> : <ArrowDropDownIcon fontSize="small" />}
                        </MuiMaterials.Box>
                    )}
                    inputProps={{
                        "aria-labelledby": "scan-cables-label",
                        "aria-expanded": menuOpen,
                        "aria-haspopup": "listbox",
                    }}
                />
            </MuiMaterials.FormControl>
            <div
                ref={cableListRef}
                className="cableList"
                style={{
                    position: "fixed",
                    top: "116px",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1100,
                    display: "none",
                }}
            >
                <div
                    className="cableListBackdrop"
                    style={{
                        position: "absolute",
                        inset: 0,
                    }}
                    onMouseDown={closeCableList}
                />
                <div
                    ref={cablePanelRef}
                    className="cableListPanel hide-scrollbar"
                    style={{
                        position: "fixed",
                        zIndex: 1,
                        overflow: "auto",
                        background: getThemeColor('#ffffff', '#2d2d2d'),
                        border: `1px solid ${getThemeColor('#cccccc', 'rgba(244,204,142,0.5)')}`,
                        borderRadius: "4px",
                        boxShadow: getThemeColor(
                            "0px 5px 15px rgba(0,0,0,0.12)",
                            "0px 5px 15px rgba(0,0,0,0.45)",
                        ),
                        padding: "8px 12px 8px 8px",
                        boxSizing: "border-box",
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                <MuiMaterials.ThemeProvider theme={theme} key={`cablepicker-theme-${global.gw.theme}-${forceUpdate}`}>
                    <div>
                        <MuiMaterials.FormControlLabel
                            sx={{
                                color: getThemeColor('#333333', '#f4cc8e'),
                                margin: "0px",
                                width: "fit-content",
                                maxWidth: "100%",
                            }}
                            control={<MuiMaterials.Checkbox
                                key={`cablepicker-all-checkbox-${global.gw.theme}-${forceUpdate}`}
                                checked={props.allCheckboxChecked !== undefined ? props.allCheckboxChecked : allCheckboxChecked}
                                sx={{
                                    color: getThemeColor('#333333', '#f4cc8e'),
                                    '&.Mui-checked': {
                                        color: getThemeColor('#333333', '#f4cc8e'),
                                    },
                                    padding: "1px 0",
                                }}
                                onChange={e => {
                                    let tempChecked = [...allChecked];
                                    
                                    // If bins are selected, only update cables in those bins
                                    if (props.selectedBins && props.selectedBins.length > 0 && getCableIdsInSelectedBins.size > 0) {
                                        // Only update checked state for cables in selected bins
                                        getCableIdsInSelectedBins.forEach(cableId => {
                                            const idx = cables.findIndex(c => c.id === cableId);
                                            if (idx >= 0) {
                                                tempChecked[idx] = e.target.checked;
                                            }
                                        });
                                    } else {
                                        // No bins selected, update all cables
                                        for (var i = 0; i < allChecked.length; i++) {
                                            tempChecked[i] = e.target.checked;
                                        }
                                    }
                                    
                                    setAllChecked(tempChecked);
                                    const newChecked = e.target.checked;
                                    setAllCheckboxChecked(newChecked);
                                    // Sync with parent and bin picker
                                    if (props.onAllCheckboxChange) {
                                        props.onAllCheckboxChange(newChecked);
                                    }
                                    if (e.target.checked) {
                                        // Add cable names
                                        const cableIdsToAdd = props.selectedBins && props.selectedBins.length > 0 && getCableIdsInSelectedBins.size > 0
                                            ? Array.from(getCableIdsInSelectedBins)
                                            : cables.map(c => c.id);
                                        const namesToAdd = cables.filter(c => cableIdsToAdd.includes(c.id)).map(c => c.name);
                                        setSelectedNames(prev => {
                                            const newNames = [...prev];
                                            namesToAdd.forEach(name => {
                                                if (!newNames.includes(name)) {
                                                    newNames.push(name);
                                                }
                                            });
                                            return newNames;
                                        });
                                    }
                                    else {
                                        // Remove cable names
                                        const cableIdsToRemove = props.selectedBins && props.selectedBins.length > 0 && getCableIdsInSelectedBins.size > 0
                                            ? Array.from(getCableIdsInSelectedBins)
                                            : cables.map(c => c.id);
                                        setSelectedNames(prev => prev.filter(name => {
                                            const cable = cables.find(c => c.name === name);
                                            return !cable || !cableIdsToRemove.includes(cable.id);
                                        }));
                                    }
                                    
                                    if (props && props.selectedAll) {
                                        props.selectedAll(e.target.checked);
                                    }
                                    e.preventDefault = true;
                                    return false;
                                }}
                            />}
                            label={"All"}
                            id={"checkAllCables"}
                        />
                    </div>
                <MuiMaterials.FormGroup sx={{ alignItems: "flex-start" }}>
                    {filteredCables.map((cable, b) => {
                        // Find the index in the original cables array for the checked state
                        const originalIndex = cables.findIndex(c => c.id === cable.id);
                        return (
                        <MuiMaterials.FormControlLabel
                            key={`cablepicker-cable-${cable.id}-${global.gw.theme}-${forceUpdate}`}
                            sx={{
                                color: getThemeColor('#333333', '#f4cc8e'),
                                margin: "0px",
                                width: "fit-content",
                                maxWidth: "100%",
                            }}
                            control={<MuiMaterials.Checkbox
                                className="chbBin"
                                defaultChecked
                                checked={originalIndex >= 0 ? allChecked[originalIndex] : false}
                                sx={{
                                    color: getThemeColor('#333333', '#f4cc8e'),
                                    '&.Mui-checked': {
                                        color: getThemeColor('#333333', '#f4cc8e'),
                                    },
                                    padding: "1px 0",
                                }}
                                onChange={e => {
                                    let tempChecked = [...allChecked];
                                    tempChecked[originalIndex] = e.target.checked;
                                    setAllChecked(tempChecked);
                                    
                                    // Update allCheckboxChecked based on cables in selected bins
                                    if (props.selectedBins && props.selectedBins.length > 0 && getCableIdsInSelectedBins.size > 0) {
                                        const allCablesInSelectedBinsChecked = Array.from(getCableIdsInSelectedBins).every(cableId => {
                                            const idx = cables.findIndex(c => c.id === cableId);
                                            return idx >= 0 && tempChecked[idx] === true;
                                        });
                                        setAllCheckboxChecked(allCablesInSelectedBinsChecked);
                                    } else {
                                        // No bins selected - check if ALL cables are checked
                                        const allCablesChecked = cables.length > 0 && tempChecked.length === cables.length && tempChecked.every(checked => checked === true);
                                        setAllCheckboxChecked(allCablesChecked);
                                    }
                                    
                                    let tempNames = [...selectedNames];
                                    if (e.target.checked) {
                                        if (!tempNames.includes(cable.name)) {
                                            tempNames.push(cable.name);
                                        }
                                    }
                                    else {
                                        for (let i = 0; i < tempNames.length; i++) {
                                            let bn = tempNames[i];
                                            if (bn === cable.name) {
                                                tempNames.splice(i, 1);
                                                break;
                                            }
                                        }
                                    }
                                    setSelectedNames(tempNames);
                                    setIsArrayChanged(!isArrayChanged);

                                    if (props && props.selected) {
                                        props.selected(cable.id, e.target.checked);
                                    }

                                }}
                            />}
                            label={cable.name}
                            inputProps={{ 'aria-label': 'controlled' }}
                        />
                        );
                    })}
                </MuiMaterials.FormGroup>
            </MuiMaterials.ThemeProvider>
                </div>
            </div>
        </MuiMaterials.Box>
    );
}
export default CablePicker;